// W36: thin wrapper over the generated inbox client.
//
// Types come from `./generated/inbox` (regenerate with
// `pnpm --filter @repletoryreach/sdk-api generate inbox`); the request path
// stays on the house core so the envelope, cursor pagination, Idempotency-Key,
// token refresh and ApiError mapping behave exactly as before.
//
// W46: `services/inbox` now uses the house error envelope everywhere. Its
// 4xx bodies were bare strings (`{"error":"invalid_state"}`,
// `{"error":"shortcut_taken"}`) and the lock 409 was unenveloped altogether;
// all of them are now `{error:{code,message}}`, so `fromErrorBody` lifts the
// real code onto `ApiError.code`. The lock 409 keeps `holder`, `holder_name`,
// `since` and `expires_at` BESIDE the envelope so the console's lock banner
// still knows who holds it; those stay on `ApiError.body`, not
// `ApiError.details`.

import { http } from './_core/request.js';
import type { DashboardStat } from './_core/dashboard.js';
import { isApiError } from './_core/errors.js';
import type { Envelope } from './_core/envelope.js';
import type { Page, PageOpts } from './_core/pagination.js';
import { buildPageQuery } from './_core/pagination.js';
import type * as gen from './generated/inbox/index.js';

// ── Conversations ────────────────────────────────────────────────────────

/**
 * W9: latest SLA timer for a conversation, embedded read-only on list and
 * detail rows (`null` when no active policy matched). `started_at` is the
 * timer's materialisation time: the 0% point for the badge maths; the
 * server marks `breached_at` once `first_response_due_at` passes without a
 * first response.
 *
 * The four "not yet" stamps stay optional. The generated `ConversationSlaDto`
 * marks them required-nullable (the projection always emits them), but this
 * client has always declared them optional and the consoles' SLA fixtures
 * (`apps/tenant-ui/.../SlaBadge.test.tsx`) build partial timers against that
 * shape.
 */
export type ConversationSla = Omit<
  gen.ConversationSlaDto,
  'first_response_at' | 'resolve_due_at' | 'resolved_at' | 'breached_at'
> & {
  first_response_at?: string | null;
  resolve_due_at?: string | null;
  resolved_at?: string | null;
  breached_at?: string | null;
};

/**
 * The four codes `POST /v1/conversations/{id}/state` accepts
 * (`ConversationStateCodes` in `services/inbox/Domain`). Hand-written: the
 * service types every state field as a plain `string`, so OpenAPI has no
 * union to generate from.
 */
export type ConversationStateCode = 'open' | 'pending' | 'resolved' | 'archived';

/**
 * W9; live edit lock on a conversation (30 s TTL, refreshed by re-locking).
 * Present on the detail response only while unexpired; `is_mine` is relative
 * to the caller's user id.
 */
export type ConversationLock = gen.ConversationLockDto;

/**
 * The body of the 409 from `lockConversation`. W46`error` is now the house
 * `{code:"conversation_locked", message}` object, so `ApiError.code` reads
 * `conversation_locked`. The four holder fields sit BESIDE it at the top
 * level, which is why they arrive on `ApiError.body` rather than
 * `ApiError.details`.
 */
export type ConversationLockConflict = Omit<gen.ConversationLockConflict, 'error'> & {
  error: { code: string; message?: string | null };
};

/** The 409 code `lockConversation` throws when another agent holds the lock. */
export const CONVERSATION_LOCKED = 'conversation_locked';

/**
 * Reads the holder details off a `lockConversation` 409. Returns `null` for
 * anything else, so a caller can render "held by X" without type-testing the
 * raw body itself.
 */
export function lockConflictOf(err: unknown): Omit<ConversationLockConflict, 'error'> | null {
  if (!isApiError(err) || err.status !== 409) return null;
  const body = err.body as Partial<ConversationLockConflict> | null;
  if (!body || typeof body !== 'object' || body.holder === undefined) return null;
  return {
    holder: body.holder,
    holder_name: body.holder_name ?? null,
    since: body.since as string,
    expires_at: body.expires_at as string
  };
}

/**
 * Which messaging surface a conversation is on.
 *
 * The same human on two channels is two conversations, because they are two
 * ids Meta issues separately and the platform has not been told they are one
 * person. Nothing in this SDK merges them, and nothing should.
 */
/**
 * `voice` (2 October 2026): phone calls from Reach Voice, one message per call (`msg_type` `call`, with
 * `meta_message_id` `voice-call:<id>`). Read-only: a reply is refused with 409 `CONVERSATION_READ_ONLY`.
 *
 * `web` (3 October 2026): the website chat. The account is the website chat, the contact the visitor's
 * session id; there is no messaging window, and a reply waits until the visitor next has the chat open.
 *
 * `email` (4 October 2026): email threads. The contact is the customer's address (`contact_ref`), the thread
 * has a `subject`, there is no messaging window (the server always answers open), there is no bot on it, and
 * {@link replyToConversation} sends the reply as an email threaded under the customer's last one.
 */
export type ConversationChannel = 'whatsapp' | 'messenger' | 'instagram' | 'voice' | 'web' | 'email';

export type ConversationSummary = Omit<gen.ConversationListItem, 'state_code' | 'sla' | 'channel'> & {
  /**
   * ⚠ DRIFT: declared as the `ConversationStateCode` union for editor help,
   * but the service selects it out of the `conversation_states` lookup and
   * types it `string?`; an operator adding a row to that table would return a
   * code outside the union. `| string | null` keeps that honest (and is why
   * the union collapses to `string` for the compiler).
   */
  state_code?: ConversationStateCode | string | null;
  sla: ConversationSla | null;
  /**
   * `whatsapp` | `messenger` | `instagram`. Typed as the union for editor help
   * and widened to `string` because the column is a free string: a channel
   * added after this SDK was published returns a value outside the union
   * rather than an error.
   */
  channel: ConversationChannel | string;
  /** Your account the thread is on: a WhatsApp number, a Facebook page, or an Instagram account. */
  channel_account_id: string;
  /**
   * The person, as this channel names them: E.164 on WhatsApp, a page-scoped
   * PSID on Messenger, an Instagram-scoped IGSID on Instagram. `contact_msisdn`
   * carries the same value on WhatsApp and is null on the other two, which have
   * no telephone number.
   */
  contact_ref: string;
  /**
   * Whether the bot is staying out of this thread right now. A reply from the
   * console keeps it quiet until 24 hours after the team's last reply
   * (`bot_resumes_at`); a thread assigned to an agent stays quiet until it is
   * resolved or {@link setBotPaused} resumes it (`bot_resumes_at` null).
   * `bot_paused_at` is when a person last took the thread over.
   */
  bot_paused?: boolean;
  bot_paused_at?: string | null;
  bot_resumes_at?: string | null;
  /** Who took this chat over from the bot (4 October 2026); null when the bot has it. See {@link takeOverConversation}. */
  taken_over_by_user_id?: string | null;
  /** The bot it was taken from, for "Hand back to …". */
  taken_over_from?: string | null;
  /** The team blocked this customer: the bot says nothing to them and replies are refused (`CONTACT_BLOCKED`). */
  blocked?: boolean;
  /** An email thread's subject (4 October 2026); null on the chat channels. */
  subject?: string | null;
  /**
   * The team the thread belongs to (7 October 2026); null when anyone may take it. While set, the inbox
   * assigns it only to an available member of that team and leaves it unassigned when none is available.
   * See {@link setConversationTeam}.
   */
  team_id?: string | null;
};

export type ConversationDetail = Omit<gen.ConversationDetail, 'state_code' | 'sla' | 'channel'> & {
  /** ⚠ DRIFT: see `ConversationSummary.state_code`. */
  state_code?: ConversationStateCode | string | null;
  sla: ConversationSla | null;
  channel: ConversationChannel | string;
  channel_account_id: string;
  /** See {@link ConversationSummary.contact_ref}. */
  contact_ref: string;
  /** See {@link ConversationSummary.bot_paused}. */
  bot_paused?: boolean;
  bot_paused_at?: string | null;
  bot_resumes_at?: string | null;
  /** See {@link ConversationSummary.taken_over_by_user_id}. */
  taken_over_by_user_id?: string | null;
  taken_over_at?: string | null;
  taken_over_from?: string | null;
  /** See {@link ConversationSummary.blocked}. */
  blocked?: boolean;
  /** When the customer was blocked, why, by whom, and whether on WhatsApp too. Null when not blocked. */
  block?: ConversationBlock | null;
  /** See {@link ConversationSummary.subject}. */
  subject?: string | null;
  /** See {@link ConversationSummary.team_id}. */
  team_id?: string | null;
  /**
   * Where this thread stands against its channel's messaging window, and how
   * much text the composer may send.
   *
   * Answered by the server because the server is what refuses. A composer
   * greyed out on one rule while the send is refused by another is worse than
   * no composer at all.
   */
  window?: ConversationWindow | null;
};

/** Where a conversation stands against its channel's messaging window. */
export interface ConversationWindow {
  /** True when a free-form message will be delivered right now. */
  open: boolean;
  /** The newest thing this person said to us. Null when they never have. */
  last_inbound_at?: string | null;
  /** 24 hours after that. Null when there is nothing to count from. */
  expires_at?: string | null;
  /** `open` | `expired` | `never`. */
  reason: string;
  /**
   * How much text this channel takes, in BYTES of UTF-8. Instagram counts
   * bytes, not characters: 1 000 of them. The difference is invisible in
   * English and immediate in isiZulu or anything carrying an emoji.
   */
  max_text_bytes: number;
}

export type ConversationMessage = Omit<gen.ConversationMessageDto, 'direction'> & {
  /**
   * Narrowed to the two values the `messages.direction` column ever holds;
   * the generated DTO types it `string`.
   */
  direction: 'inbound' | 'outbound';
  /**
   * Set when Meta rejected the send.
   *
   * This was drift until `20260903_004_MessageFailures.sql`: the columns did
   * not exist, the projection did not select them, and `MessageSentConsumer`
   * treated any status that was not `message.delivered` as `read`, so a
   * FAILED send was recorded as one the customer had read. All three are
   * fixed; the console's failure branch is live.
   */
  failed_at?: string | null;
  /** Meta's numeric code, e.g. `"131047"` (re-engagement window closed). */
  error_code?: string | null;
  /** Meta's own sentence about the failure, shown to the agent verbatim. */
  error_title?: string | null;
  /** The subject of this email (4 October 2026); null on the chat channels. */
  subject?: string | null;
} & MessageTemplateFields;

/**
 * What a template send actually said, and the parts of it that are not words.
 *
 * A template message carries no text on the wire: Meta pastes the parameter
 * values into the wording it approved. `services/channel-wa` composes the
 * result at the moment of sending and the inbox stores it, so `body` is the
 * message rather than `[template: order_ready_5388]`, and a template edited or
 * deleted afterwards cannot change what the history says was sent.
 *
 * These are on the thread row rather than behind the details call because the
 * bubble itself has to draw them: a thread cannot wait for one request per
 * message to learn whether this one carried buttons.
 */
export interface MessageTemplateFields {
  /** Meta's template name, for a template send. Absent on everything else. */
  template_name?: string | null;
  template_language?: string | null;
  /** `marketing` | `utility` | `authentication` | `service`, as the send declared it. */
  template_category?: string | null;
  /** Absent when the template had no header, no buttons and no parameters. */
  template_facts?: TemplateFacts | null;
  /**
   * How `body` came to hold what it holds, for a template send. Absent on a
   * message that carries words of its own.
   *
   * `composed` was written at send time and is exact. `recovered` was composed
   * afterwards from the template as it stands now, so it is the probable
   * wording rather than the certain one. `unrecoverable` means the template is
   * no longer on file and `body` is null: show the reader a sentence saying so
   * rather than an empty bubble, and never invent the words.
   */
  body_source?: MessageBodySource | null;
  /**
   * The platform's own name for a delivery failure, as channel-wa's
   * `MetaRefusals.ForDelivery` translated it. Prefer it over deriving one from
   * `error_code` with `channelWa.deliveryFailure`: channel-wa matches on Meta's
   * wording as well as its number, so it recognises refusals the code alone
   * does not name.
   */
  error_refusal_code?: string | null;
  /** Meta's own message id. The one thing Meta's support asks for. */
  meta_message_id?: string | null;
  /**
   * What the chatbot made of the customer's file (27 October 2026): a voice note's transcript, what a photo shows, or
   * a document's first lines. Null for a message that is not a file, or one the chatbot did not read.
   */
  media_text?: string | null;
}

export type MessageBodySource = 'composed' | 'recovered' | 'unrecoverable';

/**
 * The parts of a template message that are not words. Kept structured rather
 * than flattened into the text so a console draws a button as a button, and
 * writes a media header's line in its own reader's language instead of
 * repeating one composed on the server.
 */
export interface TemplateFacts {
  header?: TemplateHeader | null;
  buttons?: TemplateButton[] | null;
  params?: TemplateParam[] | null;
}

/**
 * `format` is Meta's. A `TEXT` header is already the first line of `body`;
 * every other format is a file, and `link` is where it came from.
 */
export interface TemplateHeader {
  format: 'TEXT' | 'IMAGE' | 'VIDEO' | 'DOCUMENT' | 'LOCATION' | string;
  text?: string | null;
  link?: string | null;
  filename?: string | null;
}

/** `type` is Meta's: QUICK_REPLY, URL, PHONE_NUMBER, COPY_CODE, OTP or FLOW. */
export interface TemplateButton {
  type: 'QUICK_REPLY' | 'URL' | 'PHONE_NUMBER' | 'COPY_CODE' | 'OTP' | 'FLOW' | string;
  text?: string | null;
  url?: string | null;
  phone_number?: string | null;
}

/** One value substituted into the template. `index` is the `{{n}}` it filled. */
export interface TemplateParam {
  /** `header`, `body` or `button`. */
  component: string;
  index: number;
  /** `text`, or `image` / `video` / `document` for a media header. */
  kind: string;
  value: string;
}

/**
 * Everything the platform knows about one message.
 *
 * The four timestamps are the timeline and only a stage that happened carries
 * one. `read_at` deserves care: it is absent both when the recipient has not
 * read the message and when they have read receipts switched off in WhatsApp,
 * and nothing on this side can tell the two apart. Say so, rather than letting
 * a missing read be taken for indifference.
 */
export interface MessageDetail extends MessageTemplateFields {
  id: string;
  conversation_id: string;
  direction: 'inbound' | 'outbound';
  msg_type: string;
  body?: string | null;
  /** Accepted for sending. There is no separate `sent_at`: this is it. */
  created_at: string;
  delivered_at?: string | null;
  read_at?: string | null;
  failed_at?: string | null;
  /** Meta's numeric code, e.g. `"131047"`. */
  error_code?: string | null;
  /** Meta's own sentence. Kept for support; show your own translated one. */
  error_title?: string | null;
  media_id?: string | null;
  media_mime?: string | null;
  media_name?: string | null;
  media_size?: number | null;
  media_url?: string | null;
  from_msisdn?: string | null;
  to_msisdn?: string | null;
  contact_msisdn?: string | null;
  contact_display_name?: string | null;
  phone_number_id?: string | null;
  /** The person on the team who sent it. Absent for a send the platform made on their behalf. */
  sent_by_user_id?: string | null;
  /** Who caused the send: a user id, or `system:flows` / `system:broadcasts`. */
  sent_by_actor?: string | null;
  channel_send_record_id?: string | null;
  /**
   * What it cost, read from the ledger. Absent when there is no line for it or
   * the ledger could not be read, which is not the same as free: render the
   * absence as "we do not know", never as "nothing".
   */
  cost?: MessageCost | null;
}

/**
 * What one message cost. `amount_minor` is positive: the amount taken, in the
 * wallet's minor units, which the consoles show as credits (100 minor units to
 * one credit). A message Meta accepted and then did not deliver is reversed,
 * and `refunded` says so.
 */
export interface MessageCost {
  amount_minor: number;
  currency: string;
  sku_code?: string | null;
  units?: number | null;
  unit_label?: string | null;
  /** Charge per unit applied, in the entry's currency. */
  rate_minor?: number | null;
  charged_at?: string | null;
  refunded: boolean;
  refunded_minor?: number | null;
  refunded_at?: string | null;
}

/**
 * Filters for `GET /v1/conversations`. W46: every one of these is bound and
 * honoured server-side; before W46 only `limit` and `number_id` reached the
 * query, so console filtering had to happen client-side.
 *
 * The response carries `meta.total` (the filtered row count) and
 * `meta.next_cursor`, so `paginate()` walks the whole list.
 */
export interface ConversationListOpts extends PageOpts {
  /** Filter by attached phone number. WhatsApp only; a Meta thread has none. */
  number_id?: string;
  /** Narrow the queue to one surface: `whatsapp` | `messenger` | `instagram`. */
  channel?: ConversationChannel | string;
  /** Conversation state **code**: `open` | `pending` | `resolved` | `archived`. */
  state?: ConversationStateCode | string;
  /** Assigned agent. `null` (or `'none'`) selects the unassigned queue. */
  agent_user_id?: string | null;
  /** Case-insensitive substring of the contact msisdn, display name or last preview. */
  search?: string;
  /** Only one team's conversations (7 October 2026). `null` (or `'none'`) selects conversations with no team. */
  team_id?: string | null;
  /**
   * Keyset anchor: only conversations older than this one. Predates the house
   * `cursor` and still works; prefer `cursor`/`paginate()` for a full walk.
   */
  before?: string;
}

export function listConversations(opts?: ConversationListOpts): Promise<Page<ConversationSummary>> {
  const query: Record<string, string | number | undefined> = { ...buildPageQuery(opts) };
  if (opts?.number_id !== undefined) query.number_id = opts.number_id;
  if (opts?.channel !== undefined) query.channel = opts.channel;
  if (opts?.state !== undefined) query.state = opts.state;
  if (opts?.agent_user_id !== undefined) query.agent_user_id = opts.agent_user_id ?? 'none';
  if (opts?.search !== undefined) query.search = opts.search;
  if (opts?.team_id !== undefined) query.team_id = opts.team_id ?? 'none';
  if (opts?.before !== undefined) query.before = opts.before;
  return http.get<Page<ConversationSummary>>('/v1/conversations', { query });
}

export function getConversation(id: string): Promise<Envelope<ConversationDetail>> {
  return http.get<Envelope<ConversationDetail>>(`/v1/conversations/${encodeURIComponent(id)}`);
}

/** What became of a reply. */
export interface ReplyResult {
  conversation_id: string;
  /** The channel it went out on. */
  channel: ConversationChannel | string;
  /** Meta's own message id, when the channel gave one. */
  meta_message_id?: string | null;
  /**
   * True when the message went out and the ledger did not take it. Rare and
   * serious: the customer has been served and nothing was charged. Show it;
   * do not swallow it.
   */
  metering_failed: boolean;
}

/**
 * Reply in a conversation, on whichever channel it is on.
 *
 * One call for every channel, because the conversation already knows which one
 * it is on and which of your accounts it belongs to; making the caller say so
 * again would be asking them to re-derive what the server is authoritative
 * about. The channel's own rules are enforced here, so the refusal reads the
 * same whether it came from the console or from this SDK:
 *
 * - `MESSAGING_WINDOW_CLOSED` (409): Messenger and Instagram deliver a reply
 *   only within 24 hours of the person's last message. There is no way round
 *   it; since Meta withdrew three of its four message tags in April 2026 the
 *   only one left is HUMAN_AGENT, which needs its own App Review.
 * - `MESSAGE_TOO_LONG` (400): 1 000 bytes on Instagram, 2 000 on Messenger,
 *   4 096 on WhatsApp.
 *
 * A WhatsApp reply still goes through channel-wa exactly as `channelWa.sendMessage`
 * does, so nothing about that path changes by calling this instead.
 */
export function replyToConversation(id: string, text: string): Promise<Envelope<ReplyResult>> {
  return http.post<Envelope<ReplyResult>>(`/v1/conversations/${encodeURIComponent(id)}/reply`, {
    body: { text }
  });
}

export function listMessages(conversationId: string, opts?: PageOpts): Promise<Page<ConversationMessage>> {
  return http.get<Page<ConversationMessage>>(
    `/v1/conversations/${encodeURIComponent(conversationId)}/messages`,
    { query: buildPageQuery(opts) }
  );
}

/**
 * Everything the platform knows about one message: the delivery timeline, why
 * it failed, what it cost, what was sent and Meta's own message id.
 *
 * One call rather than four, because "what happened to this message?" is one
 * question. The ledger is read live inside it, so this is the only inbox read
 * that touches billing and the only one whose answer can change without the
 * message changing.
 */
export function getMessage(conversationId: string, messageId: string): Promise<Envelope<MessageDetail>> {
  return http.get<Envelope<MessageDetail>>(
    `/v1/conversations/${encodeURIComponent(conversationId)}/messages/${encodeURIComponent(messageId)}`
  );
}

export function markConversationRead(id: string): Promise<void> {
  return http.post<void>(`/v1/conversations/${encodeURIComponent(id)}/read`);
}

/** Plain reassign (no audit row: see `transferConversation` for the audited hand-over). */
export function assignConversation(id: string, agent_user_id: string | null): Promise<Envelope<gen.ConversationAssignResult>> {
  return http.post<Envelope<gen.ConversationAssignResult>>(
    `/v1/conversations/${encodeURIComponent(id)}/assign`,
    { body: { agent_user_id } }
  );
}

/** The result of {@link setConversationTeam}. */
export interface ConversationTeamResult {
  conversation_id: string;
  /** Null when the thread now has no team. */
  team_id: string | null;
}

/**
 * Put a conversation in a team, or take it out of every team with `null` (7 October 2026). Any member of the
 * organisation may. The team is one of the organisation's teams in identity: an unknown one is a 400
 * `TEAM_NOT_FOUND`, and `TEAMS_UNAVAILABLE` (503) means the teams could not be read just then. The assignee
 * is left as it is; an unassigned thread with a team goes only to an available member of that team.
 */
export function setConversationTeam(id: string, teamId: string | null): Promise<Envelope<ConversationTeamResult>> {
  return http.post<Envelope<ConversationTeamResult>>(
    `/v1/conversations/${encodeURIComponent(id)}/team`,
    { body: { team_id: teamId } }
  );
}

/**
 * The `POST /v1/conversations/{id}/state` result. `state_code` is pinned to
 * the union: the handler validates `state` against the four codes (400
 * `invalid_state` otherwise) and echoes the lower-cased input, so unlike the
 * `state_code` read off a conversation row this one really is one of the
 * four. `resolved_at` stays optional as it always has been.
 */
type StateChangeResult = Omit<gen.ConversationStateResult, 'state_code' | 'resolved_at'> & {
  state_code: ConversationStateCode;
  resolved_at?: string | null;
};

/**
 * Change the conversation state. `resolved` / `archived` close the SLA
 * resolution window (`sla.resolved_at`); reopening does not un-stamp it.
 *
 * A customer writing again reopens a resolved or archived thread by itself on every channel but voice
 * (7 October 2026): the state goes back to `open`, whoever had it keeps it, and `conversation.updated`
 * arrives with `reopened: true`.
 *
 * An unknown state is a 400 with `ApiError.code === 'invalid_state'` (W46
 * it used to be the bare `{"error":"invalid_state"}` string, which degraded to
 * `HTTP_400`).
 */
export function setConversationState(id: string, state: ConversationStateCode): Promise<Envelope<StateChangeResult>> {
  return http.post<Envelope<StateChangeResult>>(
    `/v1/conversations/${encodeURIComponent(id)}/state`,
    { body: { state } }
  );
}

/** What a take-over or hand-back did (4 October 2026). */
export interface TakeOverResult {
  conversation_id: string;
  taken_over_by_user_id: string | null;
  /** The bot's name. */
  taken_over_from: string | null;
  bot_paused: boolean;
  /** False when the customer could not be told (a closed WhatsApp window, for one); the take-over still stands. */
  notice_sent: boolean;
  notice_error: string | null;
}

/**
 * Take a chat over from the bot: it is assigned to you, the bot stays out, and the customer is told, e.g.
 * "Stanley has joined the chat and will help you from here, taking over from Jabulane." Taking over a chat
 * you already have does nothing and announces nothing. 409 `CONVERSATION_READ_ONLY` on a call record.
 */
export function takeOverConversation(id: string, personName?: string | null): Promise<Envelope<TakeOverResult>> {
  return http.post<Envelope<TakeOverResult>>(`/v1/conversations/${encodeURIComponent(id)}/takeover`, { body: { person_name: personName ?? null } });
}

/** Hand a chat back to the bot: it answers again, the customer is told, and the chat stays assigned to you. */
export function handBackConversation(id: string): Promise<Envelope<TakeOverResult>> {
  return http.post<Envelope<TakeOverResult>>(`/v1/conversations/${encodeURIComponent(id)}/handback`);
}

/** The result of {@link setBotPaused}: the thread's pause as it now stands. */
export interface BotPauseResult {
  conversation_id: string;
  bot_paused: boolean;
  bot_paused_at: string | null;
  bot_resumes_at: string | null;
}

/**
 * Pause or resume the bot on one conversation.
 *
 * Pausing keeps the bot out of the thread as a console reply would, until 24
 * hours after it (or until resolved, on a thread assigned to an agent).
 * Resuming hands the thread back to the bot at once: the next message the
 * person sends starts or continues a flow. 404 for a conversation that does
 * not exist or belongs to another organisation.
 */
export function setBotPaused(id: string, paused: boolean): Promise<Envelope<BotPauseResult>> {
  return http.post<Envelope<BotPauseResult>>(
    `/v1/conversations/${encodeURIComponent(id)}/bot`,
    { body: { paused } }
  );
}

/** A block on the customer in a conversation. */
export interface ConversationBlock {
  blocked_at: string;
  reason: string | null;
  blocked_by_name: string | null;
  blocked_on_whatsapp: boolean;
}

/** The result of {@link blockContact} and {@link unblockContact}. */
export interface BlockResult {
  conversation_id: string;
  blocked: boolean;
  blocked_at?: string;
  blocked_on_whatsapp?: boolean;
  /** Set when WhatsApp was asked and did not do it (for example, Meta only blocks someone who wrote in the last 24 hours). The platform block still holds. */
  whatsapp_error: string | null;
}

/**
 * Block the customer in a conversation (15 September 2026). The bot says
 * nothing to them and the inbox refuses to write to them. With
 * `also_on_whatsapp` the number is blocked on WhatsApp itself as well, which
 * stops them writing at all; `whatsapp_error` says when that part failed.
 */
export function blockContact(id: string, opts?: { reason?: string; also_on_whatsapp?: boolean }): Promise<Envelope<BlockResult>> {
  return http.post<Envelope<BlockResult>>(
    `/v1/conversations/${encodeURIComponent(id)}/block`,
    { body: { reason: opts?.reason ?? null, also_on_whatsapp: opts?.also_on_whatsapp ?? false } }
  );
}

/** Unblock the customer in a conversation, on WhatsApp too when the block was made there. */
export function unblockContact(id: string): Promise<Envelope<BlockResult>> {
  return http.delete<Envelope<BlockResult>>(`/v1/conversations/${encodeURIComponent(id)}/block`);
}

// ── Locks ────────────────────────────────────────────────────────────────

export type ConversationLockGrant = gen.ConversationLockGrant;

/**
 * 200 with the grant when acquired/extended; 409 when another agent holds it;
 * **404 when the conversation does not exist or belongs to another tenant**
 * (W46; this used to answer 200 with a grant for any id at all).
 *
 * On the 409 the thrown `ApiError` carries `code === 'conversation_locked'`;
 * the holder's name is on `ApiError.body.holder_name`: never on
 * `ApiError.details`. Use {@link lockConflictOf} to read it safely.
 */
export function lockConversation(id: string, holderName?: string): Promise<Envelope<ConversationLockGrant>> {
  return http.post<Envelope<ConversationLockGrant>>(
    `/v1/conversations/${encodeURIComponent(id)}/lock`,
    { body: { holder_name: holderName } }
  );
}

export function unlockConversation(id: string): Promise<void> {
  return http.delete<void>(`/v1/conversations/${encodeURIComponent(id)}/lock`);
}

// ── Agents ───────────────────────────────────────────────────────────────

/**
 * Agent profile row as the inbox service returns it. The inbox does not know
 * user emails/names: join on `user_id` with `identity.listUsers()` for display.
 */
export type Agent = gen.AgentDto;

/**
 * ⚠ DRIFT (harmless): typed `Envelope<Agent[]>`, but `GET /v1/agents`
 * answers `ListEnvelopeOfAgentDto`, i.e. `{data, meta:{count,total,limit}}`.
 * The house `Envelope.meta` is optional and open (`[key: string]: unknown`),
 * so the extra `meta` is source-compatible; it is simply not typed here.
 * Same story for `listNotes`, `listSlaPolicies` and `listTransfers` below.
 */
export function listAgents(): Promise<Envelope<Agent[]>> {
  return http.get<Envelope<Agent[]>>('/v1/agents');
}

/** PUT /v1/agents/me/availability: first call also creates the caller's agent profile. */
export function updateMyAvailability(status: string, skills?: string[]): Promise<Envelope<gen.AgentAvailabilityResult>> {
  return http.put<Envelope<gen.AgentAvailabilityResult>>('/v1/agents/me/availability', { body: { status, skills } });
}

// ── SLA ──────────────────────────────────────────────────────────────────

export type SlaPolicy = gen.SlaPolicyDto;
export type CreateSlaPolicyRequest = gen.CreateSlaPolicyRequest;
export type SlaTimer = gen.SlaTimerDto;

/** ⚠ See `listAgents` - the response carries an untyped `meta.count`. */
export function listSlaPolicies(): Promise<Envelope<SlaPolicy[]>> {
  return http.get<Envelope<SlaPolicy[]>>('/v1/sla/policies');
}

export function createSlaPolicy(req: CreateSlaPolicyRequest): Promise<Envelope<gen.SlaPolicyCreated>> {
  return http.post<Envelope<gen.SlaPolicyCreated>>('/v1/sla/policies', { body: req });
}

/** Latest SLA timer for a conversation; 404 ApiError when no policy matched it. */
export function getSlaTimer(conversationId: string): Promise<Envelope<SlaTimer>> {
  return http.get<Envelope<SlaTimer>>(`/v1/sla/timers/${encodeURIComponent(conversationId)}`);
}

// ── Agent collaboration tools (W9) ───────────────────────────────────────

export type ConversationNote = gen.ConversationNoteDto;

/** ⚠ See `listAgents` - the response carries an untyped `meta.count`. */
export function listNotes(conversationId: string): Promise<Envelope<ConversationNote[]>> {
  return http.get<Envelope<ConversationNote[]>>(`/v1/conversations/${encodeURIComponent(conversationId)}/notes`);
}

export function addNote(conversationId: string, body: string, authorName?: string): Promise<Envelope<gen.ConversationNoteCreated>> {
  return http.post<Envelope<gen.ConversationNoteCreated>>(
    `/v1/conversations/${encodeURIComponent(conversationId)}/notes`,
    { body: { body, author_name: authorName } }
  );
}

/**
 * The conversation's own tags. Note the shape difference against
 * `listOrgTags`: this route answers `{data: string[]}` with **no** `meta`
 * (`EnvelopeOfString`), while `GET /v1/conversations/tags` answers
 * `{data: OrgTagDto[], meta:{count}}`. Two routes, two generated types.
 */
export function listTags(conversationId: string): Promise<Envelope<string[]>> {
  return http.get<Envelope<string[]>>(`/v1/conversations/${encodeURIComponent(conversationId)}/tags`);
}

export function addTag(conversationId: string, tag: string): Promise<void> {
  return http.post<void>(`/v1/conversations/${encodeURIComponent(conversationId)}/tags`, { body: { tag } });
}

export function removeTag(conversationId: string, tag: string): Promise<void> {
  return http.delete<void>(
    `/v1/conversations/${encodeURIComponent(conversationId)}/tags/${encodeURIComponent(tag)}`
  );
}

/** Distinct tag used anywhere in the org, with how many conversations carry it. */
export type OrgTag = gen.OrgTagDto;

/** Mirrors `gen.InboxListOrgTagsData['query']`. */
export interface OrgTagListOpts {
  /** Case-insensitive prefix. */
  search?: string;
  limit?: number;
}

/**
 * GET /v1/conversations/tags: org-wide tag vocabulary for autocomplete
 * (most-used first). Unlike `listTags` this one is a `Page`: the handler
 * returns `{data, meta:{count}}`.
 */
export function listOrgTags(opts?: OrgTagListOpts): Promise<Page<OrgTag>> {
  const query: Record<string, string | number | undefined> = {};
  if (opts?.search !== undefined) query.search = opts.search;
  if (opts?.limit !== undefined) query.limit = opts.limit;
  return http.get<Page<OrgTag>>('/v1/conversations/tags', { query });
}

export type ConversationTransfer = gen.ConversationTransferDto;

/** Transfer = reassign + audit row; notes and tags stay with the conversation. */
export function transferConversation(conversationId: string, toUserId: string, reason?: string): Promise<Envelope<gen.ConversationTransferResult>> {
  return http.post<Envelope<gen.ConversationTransferResult>>(
    `/v1/conversations/${encodeURIComponent(conversationId)}/transfer`,
    { body: { to_user_id: toUserId, reason } }
  );
}

/** ⚠ See `listAgents` - the response carries an untyped `meta.count`. */
export function listTransfers(conversationId: string): Promise<Envelope<ConversationTransfer[]>> {
  return http.get<Envelope<ConversationTransfer[]>>(`/v1/conversations/${encodeURIComponent(conversationId)}/transfers`);
}

// ── Canned replies (W37) ─────────────────────────────────────────────────

/**
 * Org-scoped canned reply. `shortcut` comes back normalised (lower-case, no
 * leading slash): agents type `/shortcut` in the composer to insert `body`.
 */
export type CannedReply = Omit<gen.CannedReplyDto, 'created_by'> & {
  /**
   * Kept optional. The generated DTO marks it required-nullable (the row
   * always carries the column), but this client has always declared it
   * optional and the console's picker fixtures
   * (`apps/tenant-ui/.../CannedReplyPicker.test.tsx`) omit it.
   */
  created_by?: string | null;
};

/**
 * Create/update body. Taken from `gen.CannedReplyRequest`, except that
 * `shortcut` and `body` are pinned non-null: the generated request models
 * them `string?` (the C# record's properties are nullable) but the handler
 * 400s `invalid_shortcut` / `invalid_body` on a null or blank value, so
 * `null` is never a usable argument.
 */
export type CannedReplyInput = Omit<gen.CannedReplyRequest, 'shortcut' | 'body'> & {
  /** Leading "/" and case are ignored server-side. `[a-z0-9][a-z0-9_.-]*`, max 64. */
  shortcut: string;
  /** Max 4096 chars (WhatsApp text ceiling). */
  body: string;
};

/** Mirrors `gen.InboxListCannedRepliesData['query']`. */
export interface CannedReplyListOpts {
  /** Shortcut prefix (or title substring), case-insensitive. */
  search?: string;
  limit?: number;
  /** Row offset, for a numbered pager. */
  offset?: number;
  /** Two-letter language code, so a multilingual library reads one at a time. */
  language?: string;
}

/** Error code the server returns (HTTP 409) when the shortcut is already used by a live reply in the org. */
export const CANNED_REPLY_SHORTCUT_TAKEN = 'shortcut_taken';

/**
 * True when `err` is the 409 `shortcut_taken` conflict from create/update.
 *
 * W46: the service now answers the house `{error:{code,message}}` envelope,
 * so `ApiError.code` is the arm that fires. The `err.body.error` arm is kept
 * so a client pinned to a pre-W46 service still recognises the conflict.
 */
export function isShortcutTaken(err: unknown): boolean {
  if (!isApiError(err) || err.status !== 409) return false;
  const body = err.body as { error?: unknown } | null;
  return body?.error === CANNED_REPLY_SHORTCUT_TAKEN || err.code === CANNED_REPLY_SHORTCUT_TAKEN;
}

export function listCannedReplies(opts?: CannedReplyListOpts): Promise<Page<CannedReply>> {
  const query: Record<string, string | number | undefined> = {};
  if (opts?.search !== undefined) query.search = opts.search;
  if (opts?.limit !== undefined) query.limit = opts.limit;
  if (opts?.offset !== undefined) query.offset = opts.offset;
  if (opts?.language !== undefined) query.language = opts.language;
  return http.get<Page<CannedReply>>('/v1/canned-replies', { query });
}

export function getCannedReply(id: string): Promise<Envelope<CannedReply>> {
  return http.get<Envelope<CannedReply>>(`/v1/canned-replies/${encodeURIComponent(id)}`);
}

export function createCannedReply(input: CannedReplyInput): Promise<Envelope<CannedReply>> {
  return http.post<Envelope<CannedReply>>('/v1/canned-replies', { body: input });
}

/** Omitted fields keep their current value. */
export function updateCannedReply(id: string, input: Partial<CannedReplyInput>): Promise<Envelope<CannedReply>> {
  return http.put<Envelope<CannedReply>>(`/v1/canned-replies/${encodeURIComponent(id)}`, { body: input });
}

/** Soft delete; the shortcut becomes available again immediately. */
export function deleteCannedReply(id: string): Promise<void> {
  return http.delete<void>(`/v1/canned-replies/${encodeURIComponent(id)}`);
}

/** Conversation summary. `unread` is the org-wide total, not a page's worth. */
export type InboxDashboardSummary = gen.InboxDashboardSummary & DashboardStat;

/** Open conversations now, new conversations this window vs last, org-wide unread. */
export function getConversationSummary(windowHours?: number): Promise<Envelope<InboxDashboardSummary>> {
  return http.get<Envelope<InboxDashboardSummary>>('/v1/conversations/summary', {
    query: windowHours === undefined ? undefined : { window_hours: windowHours }
  });
}

// ── Compose ──────────────────────────────────────────────────────────────
//
// Message one person, or a few named people. A broadcast names its audience by
// tag or segment; compose names people one at a time, and the message becomes
// a conversation in the inbox where the reply will arrive.
//
// Hand-written rather than generated: these two endpoints post bodies the
// generator has no schema for yet, and the window vocabulary below is worth
// pinning to a union rather than leaving as `string`.

/** How many people `compose` takes at once. Above this, send a broadcast. */
export const COMPOSE_MAX_RECIPIENTS = 50;

/** WhatsApp's customer-service window, in hours. */
export const SERVICE_WINDOW_HOURS = 24;

/**
 * Why a recipient can or cannot be sent free-form text.
 *
 * - `open` they wrote to us inside the last 24 hours, so plain text will send.
 * - `expired` they wrote, but longer ago than that.
 * - `never` they have never written, so no window has ever opened.
 */
export type ServiceWindowReason = 'open' | 'expired' | 'never';

/**
 * Where one recipient stands against WhatsApp's 24-hour rule.
 *
 * `window_open` is the only field needed to decide; the rest exist so a person
 * can be told why. `msisdn` is the canonical `+27821110001` form the platform
 * stores and `requested` is the spelling that was asked about, which differ
 * only when the caller sent a number in local form.
 */
export interface RecipientWindow {
  msisdn: string;
  requested: string;
  contact_id?: string | null;
  contact_name?: string | null;
  /** Whether this number is in the organisation's contact book at all. */
  known: boolean;
  /** False once they have asked to stop hearing from this organisation. */
  opted_in: boolean;
  conversation_id?: string | null;
  phone_number_id?: string | null;
  last_inbound_at?: string | null;
  /** 24 hours after `last_inbound_at`; absent when they have never written. */
  window_expires_at?: string | null;
  window_open: boolean;
  reason: ServiceWindowReason;
}

/** `meta` on the window response: the tallies a screen renders without counting. */
export interface RecipientWindowMeta {
  count: number;
  open: number;
  closed: number;
  opted_out: number;
  /** True only when EVERY recipient is inside the window: one message goes to all of them. */
  free_text_allowed: boolean;
  window_hours: number;
}

export interface RecipientWindowPage {
  data: RecipientWindow[];
  meta: RecipientWindowMeta;
}

/**
 * Where these people stand against the 24-hour window, in one call.
 *
 * Ask as recipients are picked, before a word is typed: "you cannot send this"
 * is worth knowing while the selection is still being made. Numbers may be
 * given in local form (`0821110001`); they come back canonical.
 */
export function conversationWindow(msisdns: string[], numberId?: string): Promise<RecipientWindowPage> {
  return http.post<RecipientWindowPage>('/v1/conversations/window', {
    body: { msisdns, ...(numberId ? { number_id: numberId } : {}) }
  });
}

/** A value for one of a template's `{{n}}` placeholders, in Meta's own shape. */
export interface ComposeTemplateParameter {
  /** `text`, `image`, `video` or `document`. */
  type: string;
  text?: string | null;
  image?: { link?: string | null; id?: string | null } | null;
  video?: { link?: string | null; id?: string | null } | null;
  document?: { link?: string | null; id?: string | null } | null;
}

/** One part of the template being filled in: `header`, `body` or `button`. */
export interface ComposeTemplateComponent {
  type: string;
  parameters?: ComposeTemplateParameter[] | null;
}

export interface ComposeTemplate {
  /** The template's `code`, which is Meta's name for it. */
  name: string;
  /** e.g. `en_US`. */
  language: string;
  /** `marketing` | `utility` | `authentication` | `service`; decides the price. */
  category?: string | null;
  components?: ComposeTemplateComponent[] | null;
}

/**
 * One message to a named few.
 *
 * `type: 'text'` is refused with `WINDOW_CLOSED` unless EVERY recipient has
 * written in the last 24 hours, because one message goes to all of them.
 * `type: 'template'` sends regardless of the window.
 *
 * `number_id` may be left out when the organisation has exactly one active
 * WhatsApp number; with more than one it is required (`NUMBER_REQUIRED`),
 * because the number decides which thread the reply lands in.
 */
export interface ComposeRequest {
  recipients: string[];
  type?: 'text' | 'template';
  number_id?: string;
  text?: { body: string };
  template?: ComposeTemplate;
}

/**
 * What became of one recipient's message. A failure carries `error.code`:
 * `OPTED_OUT` (they asked to stop), `WINDOW_CLOSED` (Meta's own 131047),
 * `INSUFFICIENT_CREDITS`, `PHONE_NOT_FOUND` or `CHANNEL_UNREACHABLE`.
 */
export interface ComposeRecipientResult {
  msisdn: string;
  requested: string;
  contact_id?: string | null;
  contact_name?: string | null;
  /** The thread this landed in. Absent when the send failed before one was made. */
  conversation_id?: string | null;
  status: 'sent' | 'failed';
  meta_message_id?: string | null;
  error?: { code: string; message?: string | null } | null;
}

export interface ComposeResult {
  type: string;
  number_id: string;
  results: ComposeRecipientResult[];
}

export interface ComposeMeta {
  total: number;
  sent: number;
  failed: number;
}

export interface ComposeResponse {
  data: ComposeResult;
  meta: ComposeMeta;
}

/** The 400 code returned when free text was asked for outside the window. */
export const COMPOSE_WINDOW_CLOSED = 'WINDOW_CLOSED';

/**
 * The recipients a `WINDOW_CLOSED` refusal named, or `null` for any other
 * error. The list sits BESIDE the code on the error body, so a console can say
 * which people are the problem rather than making somebody guess.
 */
export function windowClosedRecipients(err: unknown): RecipientWindow[] | null {
  if (!isApiError(err) || err.code !== COMPOSE_WINDOW_CLOSED) return null;
  const body = err.body as { error?: { recipients?: RecipientWindow[] } } | null;
  return body?.error?.recipients ?? [];
}

/**
 * Send to the people named, one channel send each, and say what became of
 * every one of them.
 *
 * A partial failure resolves rather than throwing: `meta.sent` and
 * `meta.failed` are both meaningful and `data.results` carries a row per
 * person. Only a refusal of the whole send (an empty or over-long recipient
 * list, free text outside the window, no number to send from) throws.
 */
export function compose(req: ComposeRequest): Promise<ComposeResponse> {
  return http.post<ComposeResponse>('/v1/conversations/compose', { body: req });
}
